// @ts-strict-ignore
import React from "react";

import type { TextProps, TypographyPresetType, TextOptions } from ".";
import { FontSize, LetterSpacing, LineHeight, Text } from ".";
import { getResponsiveProps } from "./utils";

interface CommonStylesType {
  [key: string]: TextOptions;
}

export const CryptopediaStyles: CommonStylesType = {
  s48: {
    fontSize: FontSize.px48,
    letterSpacing: LetterSpacing["px-0.5"],
    lineHeight: LineHeight["em1.1"]
  },
  s36: {
    fontSize: FontSize.px36,
    letterSpacing: LetterSpacing["px-0.25"],
    lineHeight: LineHeight["em1.1"]
  },
  s30: {
    fontSize: FontSize.px30,
    letterSpacing: LetterSpacing["px-0.25"],
    lineHeight: LineHeight["em1.1"]
  },
  s28: {
    fontSize: FontSize.px28,
    letterSpacing: LetterSpacing["px-1"],
    lineHeight: LineHeight["em1.4"]
  },
  s24: {
    fontSize: FontSize.px24,
    letterSpacing: LetterSpacing["px0"],
    lineHeight: LineHeight["em1.15"]
  },
  s20: {
    fontSize: FontSize.px20,
    letterSpacing: LetterSpacing["px0"],
    lineHeight: LineHeight["em1.15"]
  },
  s18: {
    fontSize: FontSize.px18,
    letterSpacing: LetterSpacing["px0"],
    lineHeight: LineHeight["em1.6"]
  },
  s16: {
    fontSize: FontSize.px16,
    letterSpacing: LetterSpacing["px0"],
    lineHeight: LineHeight["em1.6"]
  },
  s14: {
    fontSize: FontSize.px14,
    letterSpacing: LetterSpacing["px0"],
    lineHeight: LineHeight["em1.6"]
  },
  s12: {
    fontSize: FontSize.px12,
    letterSpacing: LetterSpacing["px0"],
    lineHeight: LineHeight["em1.6"]
  }
};

export const CryptopediaAltStyles: CommonStylesType = {
  s48: {
    fontSize: FontSize.px48,
    letterSpacing: LetterSpacing["px0"],
    lineHeight: LineHeight["em1.15"]
  },
  s36: {
    fontSize: FontSize.px36,
    letterSpacing: LetterSpacing["px0.25"],
    lineHeight: LineHeight["em1.15"]
  },
  s30: {
    fontSize: FontSize.px30,
    letterSpacing: LetterSpacing["px0.25"],
    lineHeight: LineHeight["em1.15"]
  },
  s24: {
    fontSize: FontSize.px24,
    letterSpacing: LetterSpacing["px0"],
    lineHeight: LineHeight["em1.3"]
  },
  s20: {
    fontSize: FontSize.px20,
    letterSpacing: LetterSpacing["px0"],
    lineHeight: LineHeight["em1.3"]
  },
  s18: {
    fontSize: FontSize.px18,
    letterSpacing: LetterSpacing["px0"],
    lineHeight: LineHeight["em1.3"]
  },
  s16: {
    fontSize: FontSize.px16,
    letterSpacing: LetterSpacing["px0"],
    lineHeight: LineHeight["em1.3"]
  }
};

export const TypographyPresets: { [key: string]: TypographyPresetType } = {
  Heading1: {
    sm: CryptopediaStyles.s30,
    md: CryptopediaStyles.s36,
    lg: CryptopediaStyles.s48
  },
  Heading2: {
    sm: CryptopediaAltStyles.s30,
    md: CryptopediaAltStyles.s36,
    lg: CryptopediaAltStyles.s48
  },
  Heading3: {
    sm: CryptopediaStyles.s20,
    md: CryptopediaStyles.s24,
    lg: CryptopediaStyles.s36
  },
  Heading4: {
    sm: CryptopediaStyles.s18,
    md: CryptopediaStyles.s20,
    lg: CryptopediaStyles.s24
  },
  Heading5: {
    sm: CryptopediaAltStyles.s18,
    md: CryptopediaAltStyles.s20,
    lg: CryptopediaAltStyles.s24
  },
  Heading6: {
    sm: CryptopediaAltStyles.s16,
    md: CryptopediaAltStyles.s18,
    lg: CryptopediaAltStyles.s18
  },
  Paragraph1: {
    sm: CryptopediaStyles.s14,
    md: CryptopediaStyles.s16,
    lg: CryptopediaStyles.s18
  },
  Paragraph2: {
    sm: CryptopediaStyles.s12,
    md: CryptopediaStyles.s14,
    lg: CryptopediaStyles.s16
  },
  Paragraph3: {
    sm: CryptopediaStyles.s12,
    md: CryptopediaStyles.s12,
    lg: CryptopediaStyles.s14
  },
  Paragraph4: {
    sm: CryptopediaStyles.s12
  }
};

export const Heading1 = ({
  as = "h1",
  sm,
  md,
  lg,
  bold = true,
  ...props
}: TextProps) => (
  <Text
    bold={bold}
    as={as}
    {...{
      ...getResponsiveProps(TypographyPresets.Heading1, { sm, md, lg }),
      ...props
    }}
  />
);

export const Heading2 = ({
  as = "h2",
  sm,
  md,
  lg,
  bold = true,
  ...props
}: TextProps) => (
  <Text
    bold={bold}
    as={as}
    {...{
      ...getResponsiveProps(TypographyPresets.Heading2, { sm, md, lg }),
      ...props
    }}
  />
);

export const Heading3 = ({ as = "h3", sm, md, lg, ...props }: TextProps) => (
  <Text
    as={as}
    {...{
      ...getResponsiveProps(TypographyPresets.Heading3, { sm, md, lg }),
      ...props
    }}
  />
);

export const Heading4 = ({ as = "h4", sm, md, lg, ...props }: TextProps) => (
  <Text
    as={as}
    {...{
      ...getResponsiveProps(TypographyPresets.Heading4, { sm, md, lg }),
      ...props
    }}
  />
);

export const Heading5 = ({ as = "h5", sm, md, lg, ...props }: TextProps) => (
  <Text
    as={as}
    {...{
      ...getResponsiveProps(TypographyPresets.Heading5, { sm, md, lg }),
      ...props
    }}
  />
);

export const Heading6 = ({ as = "h5", sm, md, lg, ...props }: TextProps) => (
  <Text
    as={as}
    {...{
      ...getResponsiveProps(TypographyPresets.Heading6, { sm, md, lg }),
      ...props
    }}
  />
);

export const Paragraph1 = ({ as = "p", sm, md, lg, ...props }: TextProps) => (
  <Text
    as={as}
    {...{
      ...getResponsiveProps(TypographyPresets.Paragraph1, { sm, md, lg }),
      ...props
    }}
    marginBottom="8px"
  />
);

export const Paragraph2 = ({ as = "p", sm, md, lg, ...props }: TextProps) => (
  <Text
    as={as}
    {...{
      ...getResponsiveProps(TypographyPresets.Paragraph2, { sm, md, lg }),
      ...props
    }}
  />
);

export const Paragraph3 = ({ as = "p", sm, md, lg, ...props }: TextProps) => (
  <Text
    as={as}
    {...{
      ...getResponsiveProps(TypographyPresets.Paragraph3, { sm, md, lg }),
      ...props
    }}
  />
);

export const Paragraph4 = ({ as = "p", sm, md, lg, ...props }: TextProps) => (
  <Text
    as={as}
    {...{
      ...getResponsiveProps(TypographyPresets.Paragraph4, { sm, md, lg }),
      ...props
    }}
  />
);
